"use strict";

var assertion = require("./utils/assertion");

// Load Globalize main module.
var Globalize = require("globalize/dist/globalize");

// Extend Globalize with number and currency modules.
require("globalize/dist/globalize/number");

var basic = require("./utils/basic");
var objectAssign = require("object-assign");

var assert = assertion.assert;
var assertMetadata = assertion.assertMetadata;
var assertRange = assertion.assertRange;
var assertType = assertion.assertType;
var getValueOrDefault = basic.getValueOrDefault;

// Load functions shared with Node
var numberHelpers = require("./utils/numberHelpers");
var assertCanonicalNumberType = numberHelpers.assertCanonicalNumberType;
var assertLocalizedNumberType = numberHelpers.assertLocalizedNumberType;
var setFractionDigits = numberHelpers.setFractionDigits;

function assertRangeCurrencyCode(handler, parameter) {
  assertRange(
    parameter,
    handler.metadata.cldrCurrencies,
    'The ISO currency code `"{parameterValue}"` is invalid',
    { parameterValue: parameter }
  );
}

var NumberHandler = function() {};

var optionsPrototype = {
  parts: function() {
    this.outputType = "parts";
    return this;
  },
  currencyDisplay: function(currencyDisplay) {
    this.currencyDisplay = currencyDisplay;
    return this;
  }
};

Object.defineProperty(NumberHandler.prototype, "NUMBER_FORMAT", {
  get: function() {
    return {
      style: "decimal",
      maximumFractionDigits: 9,
      minimumFractionDigits: 0
    };
  },
  enumerable: true
});
var NUMBER_FORMAT = NumberHandler.prototype.NUMBER_FORMAT;

Object.defineProperty(NumberHandler.prototype, "NUMBER_FORMAT_PERCENT", {
  get: function() {
    return {
      style: "percent",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    };
  },
  enumerable: true
});

// define currency presets on handler
var CURRENCY_PRESETS = {
  CURRENCY_FORMAT: "symbol",
  CURRENCY_FORMAT_ISOCURRENCY: "code",
  CURRENCY_FORMAT_SYMBOLISOCURRENCY: "international"
};
Object.keys(CURRENCY_PRESETS).forEach(function(presetName) {
  Object.defineProperty(NumberHandler.prototype, presetName, {
    get: function() {
      var presetValue = CURRENCY_PRESETS[presetName];
      return Object.create(optionsPrototype).currencyDisplay(presetValue);
    },
    enumerable: true
  });
});
// get array of values for assertRange
var CURRENCY_PRESET_VALUES = Object.keys(CURRENCY_PRESETS).map(function(
  presetName
) {
  return CURRENCY_PRESETS[presetName];
});

/**
 * formatNumber
 */
Object.defineProperty(NumberHandler.prototype, "formatNumber", {
  value: function formatNumber(number, options) {
    options = getValueOrDefault(options, NUMBER_FORMAT);
    number = assertCanonicalNumberType(number, "number");
    assertType(options, "options", ["plain object"]);
    assert(
      options.currencyDisplay === undefined,
      "Invalid `options` property `currencyDisplay`. Use `.formatCurrency()` instead"
    );
    options = objectAssign({}, options);
    setFractionDigits(options);
    return this.globalize.formatNumber(number, options);
  },
  enumerable: true
});

/**
 * normalizeNumber
 */
Object.defineProperty(NumberHandler.prototype, "normalizeNumber", {
  value: function normalizeNumber(number, options) {
    options = getValueOrDefault(options, NUMBER_FORMAT);
    assertLocalizedNumberType(number, "number");
    assertType(options, "options", ["plain object"]);
    if (typeof number === "number") {
      return number;
    }
    return this.globalize.parseNumber(number, options);
  },
  enumerable: true
});

function numberStyles(handler) {
  return handler.metadata.cldrNumbers["symbols-numberSystem-latn"];
}

function symbolForCurrency(handler, currencyCode) {
  var currencyMeta = handler.metadata.cldrCurrencies[currencyCode];
  return currencyMeta && currencyMeta.symbol;
}

function currencyStyles(handler, currency) {
  var newCurrencyStyles = {},
    supportedStyles = [
      "international",
      "code",
      "plural",
      "separator",
      "symbol"
    ];
  supportedStyles.forEach(function(style) {
    newCurrencyStyles[style] = currencyPatternByStyle(
      handler,
      { currencyDisplay: style },
      currency
    );
  });
  return newCurrencyStyles;
}

function currencyPatternByStyle(handler, options, currency) {
  var metadata = handler.metadata,
    currencyStyles = metadata.currencyStyles,
    didCurrencyLevelOverride = false; // set this to true if the style was overriden at the currency level

  if (!options) {
    options = { currencyDisplay: "symbol" };
  }

  var style = options.currencyDisplay;
  var pattern = currencyStyles[style];

  // Check if there's an override by currency (e.g. TRY) for this particular style and apply it
  if (
    currencyStyles.currency_override &&
    currencyStyles.currency_override[currency] &&
    currencyStyles.currency_override[currency][style]
  ) {
    pattern = currencyStyles.currency_override[currency][style];
    didCurrencyLevelOverride = true; // set this flag so fuzzyMatch will be skipped
  }

  // if there was no override at the currency level
  // and if this is the international style
  // then apply fuzzyMatch
  // basically: only apply fuzzyMatch to the vanilla international style
  if (!didCurrencyLevelOverride && style === "international" && currency) {
    var currencySymbol = symbolForCurrency(handler, currency);
    if (!currencySymbol) {
      return;
    }
    if (currencySymbol.length > 1) {
      // call fuzzyMatch passing the currency Code MXN and currency Symbol $MX
      var fuzzy = fuzzyMatch(currency, currencySymbol);
      if (fuzzy >= 66) {
        pattern = pattern.replace(/¤\s*/g, "");
      }
    }
  }

  return pattern;
}

/***
 * @function
 * @name     fuzzyMatch
 * @input    it takes 2 strings valueA and valueB
 * @returns  {number} the percentage of how similar are both
 */
function fuzzyMatch(svalueA, svalueB) {
  if (svalueA === svalueB) {
    return 100;
  } else {
    var valueA = svalueA.split(""),
      valueB = svalueB.split(""),
      equivalency = 0;
    for (var i = 0; i < valueA.length; i++) {
      if (valueB.indexOf(valueA[i]) !== -1) {
        var position = valueB.indexOf(valueA[i]);
        valueB.splice(position, 1);
        equivalency++;
      }
    }
    var weight = equivalency / valueA.length;
    return weight * 100;
  }
}

function digitsForCurrency(handler, currencyCode) {
  return (
    handler.metadata.currencyInfo[currencyCode] &&
    handler.metadata.currencyInfo[currencyCode].digits
  );
}

Object.defineProperty(NumberHandler.prototype, "formatCurrency", {
  value: function formatCurrency(currency, options) {
    var currencyCode, currencyValue;
    options = getValueOrDefault(options, this.CURRENCY_FORMAT);

    assertType(currency, "currency", ["plain object"]);
    assertType(currency.currency, "currency.currency", ["string"]);
    currencyValue = assertCanonicalNumberType(currency.value, "currency.value");
    assertRangeCurrencyCode(this, currency.currency);

    assertType(options, "options", ["plain object"]);
    assertType(options.currencyDisplay, "options.currencyDisplay", ["string"]);
    assertRange(
      options.currencyDisplay,
      CURRENCY_PRESET_VALUES,
      'Invalid `options.currencyDisplay`: `"{parameterValue}"`',
      { parameterValue: options.currencyDisplay }
    );

    currencyCode = currency.currency;
    options = objectAssign({}, options);
    var currencyDigits = digitsForCurrency(this, currencyCode);
    setFractionDigits(options, currencyDigits);

    var localeNumberStyles = numberStyles(this),
      localeCurrencyStyles = currencyStyles(this, currencyCode),
      symbol = symbolForCurrency(this, currencyCode),
      formattedCurrency = "",
      pattern = localeCurrencyStyles[options.currencyDisplay].split(";")[0];
    if (options.outputType === "parts") {
      formattedCurrency = [];
    }

    var formatBinder = function(key, value) {
      if (options.outputType === "parts") {
        var parts = { type: key, value: value };
        formattedCurrency.push(parts);
      } else {
        formattedCurrency += value;
      }
    };

    if (currencyValue < 0) {
      pattern = localeCurrencyStyles[options.currencyDisplay].split(";")[1];
      currencyValue = currencyValue * -1;
    }
    var numberOptions = NumberHandler.prototype.NUMBER_FORMAT;
    numberOptions.maximumFractionDigits = options.maximumFractionDigits;
    numberOptions.minimumFractionDigits = options.minimumFractionDigits;
    var patternElements = {
      "-": { key: "sign", value: localeNumberStyles.minusSign },
      "¤": { key: "symbol", value: symbol },
      "#": {
        key: "value",
        value: this.formatNumber(currencyValue, numberOptions)
      },
      C: { key: "code", value: currencyCode },
      "\u00A0": { key: "separator", value: localeCurrencyStyles.separator }
    };

    for (var i = 0; i < pattern.length; i++) {
      var part = patternElements[pattern.charAt(i)] || {
        key: "literal",
        value: pattern.charAt(i)
      };
      formatBinder(part.key, part.value);
    }

    return formattedCurrency;
  },
  enumerable: true
});

function assertNumberMeta(metadata) {
  assertType(metadata, "metadata", ["plain object"]);
  assertMetadata(metadata.cldr);
  assertMetadata(metadata.cldr.main);
  assertMetadata(metadata.cldr.supplemental);
  assertMetadata(metadata.griffin);
  assertMetadata(metadata.griffin.number);

  var griffinNumberMeta = metadata.griffin.number;
  assertMetadata(griffinNumberMeta.country, ["string"]);
  assertMetadata(griffinNumberMeta.currencyInfo);
  assertMetadata(griffinNumberMeta.currencyStyles);

  // We assume the only key in 'main' is the locale.
  var cldrLocale = Object.keys(metadata.cldr.main)[0];
  assertMetadata(cldrLocale, ["string"]);
  var cldrMain = metadata.cldr.main[cldrLocale];
  assertMetadata(cldrMain);
  var cldrNumbers = cldrMain.numbers;
  assertMetadata(cldrNumbers);
  assertMetadata(cldrNumbers["symbols-numberSystem-latn"]);
  var cldrCurrencies = cldrNumbers.currencies;
  assertMetadata(cldrCurrencies);
  // assertMetadata(cldrCurrencies.styles); // now in griffin section
}

module.exports = {
  // common createHandler code can be factored out and put in core
  createHandler: function createHandler(metadata) {
    // TODO validate metadata based on schema.
    assertNumberMeta(metadata);

    var handler = new NumberHandler();

    // We assume the only key in 'main' is the locale.
    var cldrLocale = Object.keys(metadata.cldr.main)[0];
    var cldrMain = metadata.cldr.main[cldrLocale];
    var cldrNumbers = cldrMain.numbers;
    var cldrCurrencies = cldrNumbers.currencies;

    var griffinNumberMeta = metadata.griffin.number;

    // store particular parts of client metadata for use in the module
    handler.metadata = {
      country: griffinNumberMeta.country,
      currencyStyles: griffinNumberMeta.currencyStyles,
      currencyInfo: griffinNumberMeta.currencyInfo, // includes digits information
      cldrNumbers: cldrNumbers,
      cldrCurrencies: cldrCurrencies
    };

    // pass the cldr portion to Globalize
    Globalize.load(metadata.cldr);
    handler.globalize = Globalize(cldrLocale);

    return handler;
  }
};



//////////////////
// WEBPACK FOOTER
// /donatenodeweb-ci-2793/workspace/donate-gro-release/~/griffin-paypal/lib/client/number.js
// module id = 252
// module chunks = 4 5 6 7 8 9 10